import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Sparkles, Import as ImportIcon, Globe, Database, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function Prospecting() {
  const [mode, setMode] = useState("simulated"); // simulated | live
  const [filters, setFilters] = useState({ title: "", industry: "any", region: "", tech: "", employees: "any", min_intent: 0 });
  const [meta, setMeta] = useState({ industries: [], employees: [] });
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [liveDomain, setLiveDomain] = useState("");
  const [liveConfigured, setLiveConfigured] = useState(true);
  const [liveMessage, setLiveMessage] = useState("");

  useEffect(() => {
    api.get("/prospects/industries").then((r) => setMeta(r.data));
    doSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSearch = async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const body = {
        ...filters,
        industry: filters.industry === "any" ? "" : filters.industry,
        employees: filters.employees === "any" ? "" : filters.employees,
        limit: 40,
      };
      const { data } = await api.post("/prospects/search", body);
      setResults(data.results);
    } finally {
      setLoading(false);
    }
  };

  const doLiveSearch = async () => {
    if (!liveDomain.trim()) return toast.error("Enter a domain like stripe.com");
    setLoading(true);
    setSelected(new Set());
    setLiveMessage("");
    try {
      const { data } = await api.post("/prospects/live-search", { domain: liveDomain.trim(), limit: 20 });
      setLiveConfigured(data.configured !== false);
      if (data.configured === false) {
        setLiveMessage(data.message);
        setResults([]);
      } else if (data.error) {
        toast.error("Hunter error");
        setResults([]);
      } else {
        setResults(data.results || []);
        toast.success(`Found ${data.results?.length || 0} contacts at ${data.company || liveDomain}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const importAll = async () => {
    if (!selected.size) return toast.error("Select at least one prospect");
    try {
      if (mode === "simulated") {
        await api.post("/prospects/import", { external_ids: Array.from(selected) });
      } else {
        const chosen = results.filter((r) => selected.has(r.external_id));
        await api.post("/prospects/live-import", { prospects: chosen });
      }
      toast.success(`${selected.size} prospects added to your leads`);
      setSelected(new Set());
    } catch {
      toast.error("Import failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="prospecting-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Prospecting</h1>
          <p className="mt-1 text-sm text-slate-400">Search seeded ICP or query live company data via Hunter.io.</p>
        </div>
        <div className="inline-flex items-center gap-1 rounded-full border border-white/10 p-1 bg-white/[0.03]" data-testid="prospect-mode-toggle">
          <button onClick={() => setMode("simulated")} data-testid="prospect-mode-simulated" className={`px-4 h-9 text-xs rounded-full transition font-medium flex items-center gap-1.5 ${mode === "simulated" ? "bg-white text-slate-950" : "text-slate-300 hover:text-white"}`}>
            <Database className="w-3 h-3" /> Simulated (240)
          </button>
          <button onClick={() => setMode("live")} data-testid="prospect-mode-live" className={`px-4 h-9 text-xs rounded-full transition font-medium flex items-center gap-1.5 ${mode === "live" ? "bg-white text-slate-950" : "text-slate-300 hover:text-white"}`}>
            <Globe className="w-3 h-3" /> Live · Hunter.io
          </button>
        </div>
      </div>

      {mode === "simulated" ? (
        <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 space-y-4" data-testid="prospect-simulated-filters">
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Title contains</Label>
              <Input data-testid="prospect-filter-title" value={filters.title} onChange={(e) => setFilters({ ...filters, title: e.target.value })} placeholder="VP Sales" className="bg-white/[0.03] border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Industry</Label>
              <Select value={filters.industry} onValueChange={(v) => setFilters({ ...filters, industry: v })}>
                <SelectTrigger data-testid="prospect-filter-industry" className="bg-white/[0.03] border-white/10 mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  {meta.industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Region</Label>
              <Input data-testid="prospect-filter-region" value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })} placeholder="San Francisco" className="bg-white/[0.03] border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Tech stack</Label>
              <Input data-testid="prospect-filter-tech" value={filters.tech} onChange={(e) => setFilters({ ...filters, tech: e.target.value })} placeholder="Salesforce" className="bg-white/[0.03] border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Employees</Label>
              <Select value={filters.employees} onValueChange={(v) => setFilters({ ...filters, employees: v })}>
                <SelectTrigger data-testid="prospect-filter-employees" className="bg-white/[0.03] border-white/10 mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  {meta.employees.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Min intent</Label>
              <Input data-testid="prospect-filter-intent" type="number" min={0} max={100} value={filters.min_intent} onChange={(e) => setFilters({ ...filters, min_intent: Number(e.target.value) })} className="bg-white/[0.03] border-white/10 mt-1" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500 font-mono">{loading ? "Searching…" : `${results.length} matches`}</div>
            <div className="flex gap-2">
              <Button onClick={doSearch} data-testid="prospect-search-btn" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold rounded-full"><Search className="w-4 h-4 mr-1" /> Search</Button>
              <Button onClick={importAll} disabled={!selected.size} data-testid="prospect-import-btn" className="bg-emerald-500 hover:bg-emerald-400 disabled:bg-white/[0.03] disabled:text-slate-500 text-slate-950 font-semibold rounded-full"><ImportIcon className="w-4 h-4 mr-1" /> Import {selected.size ? `(${selected.size})` : ""}</Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 space-y-3" data-testid="prospect-live-panel">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs uppercase tracking-widest text-slate-500">Company domain</Label>
              <Input data-testid="prospect-live-domain" value={liveDomain} onChange={(e) => setLiveDomain(e.target.value)} placeholder="stripe.com" className="bg-white/[0.03] border-white/10 mt-1" onKeyDown={(e) => e.key === "Enter" && doLiveSearch()} />
            </div>
            <Button onClick={doLiveSearch} data-testid="prospect-live-search-btn" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold rounded-full h-11"><Search className="w-4 h-4 mr-1" /> Query Hunter.io</Button>
            <Button onClick={importAll} disabled={!selected.size} data-testid="prospect-import-btn" className="bg-emerald-500 hover:bg-emerald-400 disabled:bg-white/[0.03] disabled:text-slate-500 text-slate-950 font-semibold rounded-full h-11"><ImportIcon className="w-4 h-4 mr-1" /> Import {selected.size ? `(${selected.size})` : ""}</Button>
          </div>
          {!liveConfigured && liveMessage && (
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-amber-200 flex items-start gap-2" data-testid="prospect-live-not-configured">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>{liveMessage} <a href="/dashboard/settings" className="underline">Set it in Settings →</a></div>
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {results.map((p) => {
          const isSel = selected.has(p.external_id);
          return (
            <label key={p.external_id} data-testid={`prospect-row-${p.external_id}`} className={`cursor-pointer rounded-xl border ${isSel ? "border-sky-400/60 bg-sky-500/5" : "border-white/5 bg-white/[0.02]"} p-4 hover:border-sky-400/40 transition block`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <Checkbox checked={isSel} onCheckedChange={() => toggle(p.external_id)} className="mt-1" />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 truncate">{p.title} · {p.company}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{p.industry} · {p.region}</div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-400/20 font-mono text-[10px]">Intent {p.enrichment.intent_score}</Badge>
                  {p.source === "hunter" && <Badge variant="outline" className="text-[9px] font-mono text-sky-300 border-sky-400/30">HUNTER · {p.confidence || 0}%</Badge>}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {p.enrichment.tech_stack.map((t) => <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-300">{t}</span>)}
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-300">{p.enrichment.employees}</span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-300">{p.enrichment.revenue_estimate}</span>
              </div>
              <div className="mt-2 text-xs text-emerald-300 line-clamp-1">↳ {p.enrichment.recent_signal}</div>
            </label>
          );
        })}
        {mode === "live" && !loading && results.length === 0 && liveConfigured && (
          <div className="col-span-full rounded-xl border border-dashed border-white/10 p-6 text-center text-slate-500 text-sm flex items-center justify-center gap-2" data-testid="prospect-live-empty">
            <Sparkles className="w-4 h-4" /> Enter a domain above (e.g. <code className="text-sky-300">stripe.com</code>) to fetch contacts.
          </div>
        )}
      </div>
    </div>
  );
}
